/**
 * Switch component with Dashboard3 styling
 * Integrates with React Hook Form
 *
 * @example
 * ```tsx
 * <Switch {...register('notifications')}>
 *   Enable notifications
 * </Switch>
 * ```
 */

"use client";

import {
  Switch as ChakraSwitch,
  type SwitchProps as ChakraSwitchProps,
} from "@chakra-ui/react";
import { forwardRef } from "react";
import { useThemeTokens } from "@/lib/theme/tokens";
import { Text } from "@/components/ui";

export interface SwitchProps extends ChakraSwitchProps {}

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  ({ children, ...props }, ref) => {
    const theme = useThemeTokens();

    return (
      <ChakraSwitch
        ref={ref}
        size="md"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          flexWrap: "nowrap",
          // Override all Chakra default colors with Dashboard theme
          "& .chakra-switch__track": {
            bg: `${theme.borderGlass.default} !important`,
            transition: "all 0.18s ease-out",
            "&[data-checked]": {
              bg: `${theme.accent.primary} !important`,
            },
            // Hover state for unchecked
            "&:hover:not([data-checked]):not([data-disabled])": {
              bg: `${theme.borderGlass.default} !important`,
            },
          },
          // Thumb (sliding circle)
          "& .chakra-switch__thumb": {
            bg: "white !important",
            transition: "all 0.18s ease-out",
            boxShadow: theme.shadow.card,
          },
          // Focus state
          "&:focus-visible .chakra-switch__track": {
            boxShadow: `${theme.focusRing} !important`,
          },
          "&:focus .chakra-switch__track": {
            boxShadow: `${theme.focusRing} !important`,
          },
          // Invalid state
          "&[data-invalid] .chakra-switch__track": {
            borderColor: `${theme.accent.primary} !important`,
          },
          // Disabled state
          "&[data-disabled] .chakra-switch__track": {
            opacity: "0.5 !important",
            cursor: "not-allowed !important",
          },
          "&[data-disabled] .chakra-switch__thumb": {
            opacity: "0.5 !important",
          },
        }}
        {...props}
      >
        {children && (
          <Text
            variant="primary"
            fontSize="sm"
            ml={2}
            fontWeight="500"
            whiteSpace="nowrap"
            as="span"
          >
            {children}
          </Text>
        )}
      </ChakraSwitch>
    );
  },
);

Switch.displayName = "Switch";
